import type { RVLayout } from '../layout-managers/LayoutManager';
import type { ViewToken } from './ViewToken';
import type { ViewabilityConfig } from './ViewabilityConfig';
import { ViewabilityHelper } from './ViewabilityHelper';

/**
 * Web port of upstream FlashList's ViewabilityManager
 * (src/recyclerview/viewability/ViewabilityManager.ts). Owns one
 * ViewabilityHelper per (config, callback) pair and translates the helper's
 * integer-index callbacks into ViewToken<T> payloads for the consumer.
 *
 * Phase 6 scope is the single-callback case (`onViewableItemsChanged` +
 * `viewabilityConfig`). The upstream `viewabilityConfigCallbackPairs` field
 * isn't on our prop surface yet; if a consumer needs it, this class is the
 * extension point.
 */
export interface ViewabilityBinding<T> {
  viewabilityConfig?: ViewabilityConfig | null;
  onViewableItemsChanged?:
    | ((info: {
        viewableItems: ViewToken<T>[];
        changed: ViewToken<T>[];
      }) => void)
    | null;
}

export interface ViewabilityManagerOptions<T> {
  getData(): ReadonlyArray<T>;
  getKey(index: number): string;
}

interface ViewabilityContext {
  horizontal: boolean;
  scrollOffset: number;
  listSize: { width: number; height: number };
  getLayout: (index: number) => RVLayout | undefined;
  possiblyViewableIndices: number[];
}

export class ViewabilityManager<T> {
  private helpers: ViewabilityHelper[] = [];
  private readonly options: ViewabilityManagerOptions<T>;
  private hasInteracted = false;
  private lastContext: ViewabilityContext | null = null;

  constructor(options: ViewabilityManagerOptions<T>) {
    this.options = options;
  }

  /**
   * Swap the active (config, callback) binding. Called whenever the consumer
   * passes a new `onViewableItemsChanged` / `viewabilityConfig` prop. Tears
   * down outstanding timers in the previous helper so a debounced fire
   * doesn't escape after the consumer detached.
   */
  rebind(binding: ViewabilityBinding<T>): void {
    this.helpers.forEach(helper => helper.dispose());
    this.helpers = [];
    if (binding.onViewableItemsChanged != null) {
      const helper = this.createHelper(
        binding.viewabilityConfig,
        binding.onViewableItemsChanged,
      );
      helper.hasInteracted = this.hasInteracted;
      this.helpers.push(helper);
    }
  }

  get shouldListenToVisibleIndices(): boolean {
    return this.helpers.length > 0;
  }

  dispose(): void {
    this.helpers.forEach(helper => helper.dispose());
  }

  /**
   * Idempotent. The engine calls this on every user scroll; consumers can
   * call it explicitly via the FlashList ref. Subsequent calls are no-ops.
   * After flipping the gate, re-runs the last viewability pass so callbacks
   * gated by `waitForInteraction` fire immediately.
   */
  recordInteraction(): void {
    if (this.hasInteracted) return;
    this.hasInteracted = true;
    this.helpers.forEach(helper => {
      helper.hasInteracted = true;
    });
    if (this.lastContext) {
      this.runHelpers(this.lastContext);
    }
  }

  /**
   * Clears each helper's "last reported" memo and re-runs with the saved
   * context, so the next callback emits the full current viewable set. Used
   * by `FlashListRef.recomputeViewableItems` and after data updates.
   */
  recomputeViewableItems(): void {
    this.helpers.forEach(helper => helper.clearLastReportedViewableIndices());
    if (this.lastContext) {
      this.runHelpers(this.lastContext);
    }
  }

  /**
   * Runs the viewability pipeline against the strict viewport-intersecting
   * index set. The engine is responsible for computing those indices —
   * typically `layoutManager.findIndicesInRange(scrollOffset, scrollOffset + viewport)`
   * expanded into a flat list.
   */
  updateViewableItems(context: ViewabilityContext): void {
    if (!this.shouldListenToVisibleIndices) return;
    this.lastContext = context;
    this.runHelpers(context);
  }

  private runHelpers(context: ViewabilityContext): void {
    this.helpers.forEach(helper => {
      helper.updateViewableItems(
        context.horizontal,
        context.scrollOffset,
        context.listSize,
        context.getLayout,
        context.possiblyViewableIndices,
      );
    });
  }

  private createHelper(
    viewabilityConfig: ViewabilityConfig | null | undefined,
    onViewableItemsChanged: (info: {
      viewableItems: ViewToken<T>[];
      changed: ViewToken<T>[];
    }) => void,
  ): ViewabilityHelper {
    const toToken = (index: number, isViewable: boolean): ViewToken<T> => {
      const data = this.options.getData();
      return {
        item: data[index],
        key: this.options.getKey(index),
        index,
        isViewable,
        timestamp: Date.now(),
      };
    };
    return new ViewabilityHelper(
      viewabilityConfig,
      (indices, newlyVisibleIndices, newlyNonVisibleIndices) => {
        onViewableItemsChanged({
          viewableItems: indices.map(index => toToken(index, true)),
          changed: [
            ...newlyVisibleIndices.map(index => toToken(index, true)),
            ...newlyNonVisibleIndices.map(index => toToken(index, false)),
          ],
        });
      },
    );
  }
}
